import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus } from 'lucide-react';
import type { DepartmentDto } from '@workforce/shared';
import { ApiError, api } from '@/lib/api';
import { useDepartments, useEmployeeOptions } from '@/lib/queries';
import { t } from '@/lib/i18n';
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Modal, PageHeader, Select, Spinner, Textarea, Toggle } from '@/components/ui';

export function DepartmentsPage() {
  const qc = useQueryClient();
  const deps = useDepartments();
  const people = useEmployeeOptions();
  const [editing, setEditing] = useState<DepartmentDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<DepartmentDto | null>(null);
  const [form, setForm] = useState({ name: '', description: '', headEmployeeId: '', isActive: true });

  const open = (d: DepartmentDto | 'new') => {
    setEditing(d);
    setForm(
      d === 'new'
        ? { name: '', description: '', headEmployeeId: '', isActive: true }
        : { name: d.name, description: d.description ?? '', headEmployeeId: d.headEmployeeId ?? '', isActive: d.isActive },
    );
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { name: form.name, description: form.description, headEmployeeId: form.headEmployeeId || null, isActive: form.isActive };
      return editing === 'new' ? api.post('/departments', body) : api.patch(`/departments/${(editing as DepartmentDto).id}`, body);
    },
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/departments/${id}`),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors() : {};

  return (
    <div>
      <PageHeader
        title={t('Departments')}
        subtitle={t('Organise employees into teams')}
        actions={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => open('new')}>
            {t('Add department')}
          </Button>
        }
      />
      <ErrorBanner error={deps.error} />
      <Card bodyClass="p-0">
        {deps.isLoading ? (
          <Spinner />
        ) : !deps.data?.length ? (
          <EmptyState icon={<Building2 className="h-6 w-6" />} title={t('No departments yet')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="th">{t('Department')}</th>
                  <th className="th">{t('Head')}</th>
                  <th className="th">{t('Active employees')}</th>
                  <th className="th">{t('Status')}</th>
                  <th className="th" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deps.data.map((d) => (
                  <tr key={d.id}>
                    <td className="td">
                      <p className="font-medium text-slate-900">{d.name}</p>
                      {d.description && <p className="max-w-md truncate text-xs text-slate-500">{d.description}</p>}
                    </td>
                    <td className="td">{d.headEmployeeName ?? '—'}</td>
                    <td className="td">{d.employeeCount}</td>
                    <td className="td">{d.isActive ? <Badge tone="green">{t('Active')}</Badge> : <Badge>{t('Inactive')}</Badge>}</td>
                    <td className="td text-end">
                      <Button variant="ghost" size="sm" onClick={() => open(d)}>
                        {t('Edit')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleting(d)}>
                        {t('Delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? t('Add department') : t('Edit department')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={save.isPending} disabled={!form.name.trim()} onClick={() => save.mutate()}>
              {t('Save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ErrorBanner error={save.error && !Object.keys(fe).length ? save.error : null} />
          <Field label={t('Name')} required error={fe.name}>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </Field>
          <Field label={t('Description')} error={fe.description}>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label={t('Department head')} error={fe.headEmployeeId}>
            <Select value={form.headEmployeeId} onChange={(e) => setForm({ ...form, headEmployeeId: e.target.value })}>
              <option value="">{t('None')}</option>
              {people.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName}
                </option>
              ))}
            </Select>
          </Field>
          {editing !== 'new' && <Toggle checked={form.isActive} onChange={(v) => setForm({ ...form, isActive: v })} label={t('Active')} description={t("Inactive departments can't be chosen for new employees.")} />}
        </div>
      </Modal>
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={t('Delete {name}?', { name: deleting?.name })}
        message={t("Departments that have (or had) employees can't be deleted — deactivate them instead.")}
        confirmLabel={t('Delete')}
        danger
        loading={del.isPending}
        error={del.error}
        onConfirm={() => deleting && del.mutate(deleting.id)}
      />
    </div>
  );
}
